import React, { useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Target, TrendingUp, Users, DollarSign, Percent } from "lucide-react";

interface ConversionCalculatorProps {
  open: boolean;
  onClose: () => void;
}

const ConversionCalculator: React.FC<ConversionCalculatorProps> = ({ open, onClose }) => {
  const [values, setValues] = useState({
    visitors: "",
    leads: "",
    customers: "",
    revenue: "",
    adSpend: ""
  });

  const [results, setResults] = useState({
    visitorToLeadRate: 0,
    leadToCustomerRate: 0,
    overallConversionRate: 0,
    averageOrderValue: 0,
    returnOnAdSpend: 0,
    costPerAcquisition: 0
  });

  const handleInputChange = (field: string, value: string) => {
    setValues(prev => ({ ...prev, [field]: value }));
    calculateConversions({ ...values, [field]: value });
  };

  const calculateConversions = (inputs: typeof values) => {
    const visitors = parseFloat(inputs.visitors) || 0;
    const leads = parseFloat(inputs.leads) || 0;
    const customers = parseFloat(inputs.customers) || 0;
    const revenue = parseFloat(inputs.revenue) || 0;
    const adSpend = parseFloat(inputs.adSpend) || 0;

    // Visitor to Lead conversion rate
    const visitorToLeadRate = visitors > 0 ? (leads / visitors) * 100 : 0;
    
    // Lead to Customer conversion rate
    const leadToCustomerRate = leads > 0 ? (customers / leads) * 100 : 0;
    
    // Overall conversion rate (visitors to customers)
    const overallConversionRate = visitors > 0 ? (customers / visitors) * 100 : 0;
    
    // Average Order Value
    const averageOrderValue = customers > 0 ? revenue / customers : 0;
    
    // Return on Ad Spend (ROAS)
    const returnOnAdSpend = adSpend > 0 ? (revenue / adSpend) * 100 : 0;
    
    // Cost Per Acquisition
    const costPerAcquisition = customers > 0 ? adSpend / customers : 0;

    setResults({
      visitorToLeadRate,
      leadToCustomerRate,
      overallConversionRate,
      averageOrderValue,
      returnOnAdSpend,
      costPerAcquisition
    });
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatPercentage = (percentage: number) => {
    return `${percentage.toFixed(2)}%`;
  };

  const getConversionRating = (rate: number, type: 'visitor-lead' | 'lead-customer' | 'overall') => {
    let benchmarks: { excellent: number; good: number; average: number };
    
    switch (type) {
      case 'visitor-lead':
        benchmarks = { excellent: 5, good: 3, average: 1.5 };
        break;
      case 'lead-customer':
        benchmarks = { excellent: 20, good: 15, average: 10 };
        break;
      case 'overall':
        benchmarks = { excellent: 3, good: 2, average: 1 };
        break;
    }

    if (rate >= benchmarks.excellent) return { text: "Excellent", color: "text-green-600" };
    if (rate >= benchmarks.good) return { text: "Good", color: "text-blue-600" };
    if (rate >= benchmarks.average) return { text: "Average", color: "text-yellow-600" };
    return { text: "Needs Improvement", color: "text-red-600" };
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Target className="h-5 w-5" />
            Conversion Calculator
          </DialogTitle>
          <DialogDescription>
            Track conversion rates and optimize campaign performance
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Input Section */}
          <Card>
            <CardHeader>
              <CardTitle>Campaign Funnel Data</CardTitle>
              <CardDescription>Enter your funnel metrics to calculate conversion rates</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="visitors">Website Visitors</Label>
                <Input
                  id="visitors"
                  type="number"
                  placeholder="10000"
                  value={values.visitors}
                  onChange={(e) => handleInputChange('visitors', e.target.value)}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="leads">Leads Generated</Label>
                <Input
                  id="leads"
                  type="number"
                  placeholder="300"
                  value={values.leads}
                  onChange={(e) => handleInputChange('leads', e.target.value)}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="customers">Customers/Sales</Label>
                <Input
                  id="customers"
                  type="number"
                  placeholder="45"
                  value={values.customers}
                  onChange={(e) => handleInputChange('customers', e.target.value)}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="revenue">Total Revenue ($)</Label>
                <Input
                  id="revenue"
                  type="number"
                  placeholder="22500"
                  value={values.revenue}
                  onChange={(e) => handleInputChange('revenue', e.target.value)}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="adSpend">Ad Spend ($)</Label>
                <Input
                  id="adSpend"
                  type="number"
                  placeholder="5000"
                  value={values.adSpend}
                  onChange={(e) => handleInputChange('adSpend', e.target.value)}
                />
              </div>
            </CardContent>
          </Card>

          {/* Results Section */}
          <Card>
            <CardHeader>
              <CardTitle>Conversion Analysis</CardTitle>
              <CardDescription>Your calculated conversion rates and performance metrics</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 gap-4">
                <div className="p-4 rounded-lg bg-gray-50">
                  <div className="flex items-center gap-2 mb-2">
                    <Users className="h-5 w-5 text-blue-600" />
                    <h4 className="font-semibold">Visitor to Lead Rate</h4>
                  </div>
                  <p className="text-2xl font-bold text-gray-900">
                    {formatPercentage(results.visitorToLeadRate)}
                  </p>
                  <p className={`text-sm ${getConversionRating(results.visitorToLeadRate, 'visitor-lead').color}`}>
                    {getConversionRating(results.visitorToLeadRate, 'visitor-lead').text}
                  </p>
                </div>

                <div className="p-4 rounded-lg bg-gray-50">
                  <div className="flex items-center gap-2 mb-2">
                    <Target className="h-5 w-5 text-green-600" />
                    <h4 className="font-semibold">Lead to Customer Rate</h4>
                  </div>
                  <p className="text-2xl font-bold text-gray-900">
                    {formatPercentage(results.leadToCustomerRate)}
                  </p>
                  <p className={`text-sm ${getConversionRating(results.leadToCustomerRate, 'lead-customer').color}`}>
                    {getConversionRating(results.leadToCustomerRate, 'lead-customer').text}
                  </p>
                </div>

                <div className="p-4 rounded-lg bg-gray-50">
                  <div className="flex items-center gap-2 mb-2">
                    <Percent className="h-5 w-5 text-purple-600" />
                    <h4 className="font-semibold">Overall Conversion Rate</h4>
                  </div>
                  <p className="text-2xl font-bold text-gray-900">
                    {formatPercentage(results.overallConversionRate)}
                  </p>
                  <p className={`text-sm ${getConversionRating(results.overallConversionRate, 'overall').color}`}>
                    {getConversionRating(results.overallConversionRate, 'overall').text}
                  </p>
                </div>

                <div className="p-4 rounded-lg bg-gray-50">
                  <div className="flex items-center gap-2 mb-2">
                    <DollarSign className="h-5 w-5 text-emerald-600" />
                    <h4 className="font-semibold">Average Order Value</h4>
                  </div>
                  <p className="text-2xl font-bold text-gray-900">
                    {formatCurrency(results.averageOrderValue)}
                  </p>
                  <p className="text-sm text-gray-600">Per customer</p>
                </div>

                <div className="p-4 rounded-lg bg-gray-50">
                  <div className="flex items-center gap-2 mb-2">
                    <TrendingUp className="h-5 w-5 text-orange-600" />
                    <h4 className="font-semibold">Return on Ad Spend</h4>
                  </div>
                  <p className="text-2xl font-bold text-gray-900">
                    {formatPercentage(results.returnOnAdSpend)}
                  </p>
                  <p className="text-sm text-gray-600">ROAS percentage</p>
                </div>

                <div className="p-4 rounded-lg bg-gray-50">
                  <div className="flex items-center gap-2 mb-2">
                    <DollarSign className="h-5 w-5 text-red-600" />
                    <h4 className="font-semibold">Cost Per Acquisition</h4>
                  </div>
                  <p className="text-2xl font-bold text-gray-900">
                    {formatCurrency(results.costPerAcquisition)}
                  </p>
                  <p className="text-sm text-gray-600">Per customer</p>
                </div>
              </div>

              {/* Optimization Tips */}
              <div className="mt-6 p-4 rounded-lg bg-blue-50">
                <h4 className="font-semibold text-blue-900 mb-2">Optimization Tips</h4>
                <div className="text-sm text-blue-800 space-y-1">
                  {results.visitorToLeadRate < 3 && (
                    <p>• Improve landing page design and lead magnets to increase visitor-to-lead conversion</p>
                  )}
                  {results.leadToCustomerRate < 15 && (
                    <p>• Enhance lead nurturing and sales process to convert more leads to customers</p>
                  )}
                  {results.returnOnAdSpend < 300 && (
                    <p>• Consider optimizing ad targeting and creative to improve ROAS</p>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="flex justify-end gap-3 mt-6">
          <Button variant="outline" onClick={onClose}>
            Close
          </Button>
          <Button onClick={() => {
            setValues({
              visitors: "",
              leads: "",
              customers: "",
              revenue: "",
              adSpend: ""
            });
            setResults({
              visitorToLeadRate: 0,
              leadToCustomerRate: 0,
              overallConversionRate: 0,
              averageOrderValue: 0,
              returnOnAdSpend: 0,
              costPerAcquisition: 0
            });
          }}>
            Reset Calculator
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ConversionCalculator;